import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n, TranslationKey } from '../../contexts/I18nContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import {
  AuditIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ClientsIcon,
  CookieIcon,
  DashboardIcon,
  DevicesIcon,
  DeviceTypesIcon,
  IconProps,
  OtaIcon,
  PortabilityIcon,
  SettingsIcon,
  TemplatesIcon,
  UsersIcon,
} from './icons';
import { openCookieSettings } from '../../utils/cookieConsent';
import './Sidebar.css';

interface NavItem {
  path: string;
  labelKey: TranslationKey;
  icon: React.FC<IconProps>;
  /** Access flag from useClientAccess. Hidden entries are not a control —
      App.tsx guards the routes — but a menu of things you cannot open is
      noise. */
  visible: boolean;
  testId: string;
}

interface NavGroup {
  key: string;
  titleKey: TranslationKey;
  items: NavItem[];
}

/**
 * Navigation and nothing else. Session controls (theme, language, account,
 * sign-out) live in the top bar.
 *
 * Three states: expanded, collapsed to an icon rail (desktop, remembered),
 * and an off-canvas drawer on narrow screens. A collapsed rail widens back
 * out while the pointer is over it, so the labels are one hover away rather
 * than a click.
 */
export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen } = useLayout();
  const {
    shouldShowClientUI,
    canManageUsers,
    canViewAuditLog,
    canAccessCatalog,
    canAccessTemplates,
    canAccessOtaUpdates,
    canUsePortability,
    canAdministrate,
  } = useClientAccess();

  // A phone drawer closes itself once you have chosen where to go.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  // Typed before the map/filter below: chained on the literal, TypeScript
  // would widen the translation keys to plain strings.
  const allGroups: NavGroup[] = [
    {
      key: 'general',
      titleKey: 'nav.groupGeneral',
      items: [
        { path: '/dashboard', labelKey: 'nav.dashboard', icon: DashboardIcon, visible: true, testId: 'nav-dashboard' },
        { path: '/devices', labelKey: 'nav.devices', icon: DevicesIcon, visible: true, testId: 'nav-devices' },
        { path: '/users', labelKey: 'nav.users', icon: UsersIcon, visible: canManageUsers, testId: 'nav-users' },
      ],
    },
    {
      key: 'catalog',
      titleKey: 'nav.groupCatalog',
      items: [
        { path: '/config-templates', labelKey: 'nav.configTemplates', icon: TemplatesIcon, visible: canAccessTemplates, testId: 'nav-config-templates' },
        { path: '/device-types', labelKey: 'nav.deviceTypes', icon: DeviceTypesIcon, visible: canAccessCatalog, testId: 'nav-device-types' },
        { path: '/ota-updates', labelKey: 'nav.otaUpdates', icon: OtaIcon, visible: canAccessOtaUpdates, testId: 'nav-ota-updates' },
      ],
    },
    {
      key: 'admin',
      titleKey: 'nav.groupAdmin',
      items: [
        { path: '/clients', labelKey: 'nav.clients', icon: ClientsIcon, visible: shouldShowClientUI, testId: 'nav-clients' },
        { path: '/audit-log', labelKey: 'nav.auditLog', icon: AuditIcon, visible: canViewAuditLog, testId: 'nav-audit-log' },
        { path: '/portability', labelKey: 'nav.portability', icon: PortabilityIcon, visible: canUsePortability, testId: 'nav-portability' },
        { path: '/administration', labelKey: 'nav.administration', icon: SettingsIcon, visible: canAdministrate, testId: 'nav-administration' },
      ],
    },
  ];
  const groups = allGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.visible) }))
    .filter((group) => group.items.length > 0);

  const className = [
    'sidebar',
    sidebarCollapsed ? 'is-collapsed' : '',
    mobileNavOpen ? 'is-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const collapseLabel = sidebarCollapsed ? t('nav.expand') : t('nav.collapse');

  return (
    <>
      {mobileNavOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}

      <aside className={className} data-testid="sidebar">
        <nav className="sidebar-nav">
          {groups.map((group) => (
            <div className="sidebar-group" key={group.key}>
              <div className="sidebar-group-title">
                <span>{t(group.titleKey)}</span>
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`sidebar-item${active ? ' active' : ''}`}
                    onClick={(event) => {
                      navigate(item.path);
                      // A peeked rail stays open through `:focus-within` as
                      // long as the clicked button holds focus — so it would
                      // not shrink back when the pointer leaves, and a second
                      // click elsewhere was needed. Drop the focus once the
                      // choice is made; keyboard users tab back in as usual.
                      if (sidebarCollapsed) {
                        event.currentTarget.blur();
                      }
                    }}
                    aria-current={active ? 'page' : undefined}
                    title={sidebarCollapsed ? t(item.labelKey) : undefined}
                    data-testid={item.testId}
                  >
                    <span className="sidebar-item-icon"><Icon /></span>
                    <span className="sidebar-item-label">{t(item.labelKey)}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* "Collapse sidebar" at the bottom, as in GitLab. Desktop only — on
            a phone the drawer is either open or gone. */}
        <div className="sidebar-footer">
          {/* The cookie choice, reachable at any time (CNIL): same chrome as the toggle below. */}
          <button
            type="button"
            className="sidebar-toggle sidebar-cookies"
            onClick={(event) => { openCookieSettings(); event.currentTarget.blur(); }}
            aria-label={t('cookies.link')}
            title={t('cookies.link')}
            data-testid="btn-cookie-settings"
          >
            <CookieIcon />
            <span className="sidebar-toggle-label">{t('cookies.link')}</span>
          </button>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={(event) => {
              toggleSidebar();
              // Same as the items: a focused toggle would hold the rail open
              // through `:focus-within` right after collapsing it.
              event.currentTarget.blur();
            }}
            aria-label={collapseLabel}
            title={collapseLabel}
            data-testid="btn-sidebar-collapse"
          >
            {sidebarCollapsed ? <ChevronsRightIcon /> : <ChevronsLeftIcon />}
            <span className="sidebar-toggle-label">{collapseLabel}</span>
          </button>
        </div>
      </aside>
    </>
  );
};
